import type { FunctionComponent } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { type PhotoInfo, getPhotoImageURL, getPhotosWithTotal, getPhotos } from "../../store/task/photos.ts";
import { Icon } from "../../components/icon/icon.tsx";
import { useTranslator } from "../../hooks/i18n.ts";
import { usePageRefresh } from "../../hooks/use_page_refresh.ts";
import { refreshUserInfo } from "../../store/user_info.ts";

interface PhotoTimelineProps {
    cameraId: number;
    onSelectPhoto: (photo: PhotoInfo, target: 0 | 1) => void;
    selectedLeftPhotoId?: number;
    selectedRightPhotoId?: number;
}

const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });
};

export const PhotoTimeline: FunctionComponent<PhotoTimelineProps> = ({ cameraId, onSelectPhoto, selectedLeftPhotoId, selectedRightPhotoId }) => {
    const [photos, setPhotos] = useState<PhotoInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedPhotoId, setSelectedPhotoId] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>("");
    const [isNavigating, setIsNavigating] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [hasHistory, setHasHistory] = useState(false);
    const requestRef = useRef({ seq: 0, controller: null as AbortController | null });
    const navigationRef = useRef<AbortController | null>(null);
    const pageRef = useRef({ before: 0, after: 0, cursor: undefined as string | undefined });
    const scrollRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef(new Map<number, HTMLDivElement>());
    const t = useTranslator();

    const fetchPhotos = useCallback(async (append = false) => {
        requestRef.current.controller?.abort();
        const controller = new AbortController();
        const seq = ++requestRef.current.seq;
        requestRef.current.controller = controller;
        const isCurrent = () => requestRef.current.seq === seq && !controller.signal.aborted;
        // Only skip if cameraId is invalid (e.g. -1 or 0)
        // But if it's 0, backend might not like it or return empty.
        // Let's ensure we have a valid camera ID before fetching.
        if (cameraId <= 0) {
            setPhotos([]); // Clear photos if invalid camera
            setHasMore(false);
            setLoading(false);
            return;
        }
        
        setLoading(true);
        setLoadError("");
        try {
            if (!append && selectedDate) {
                 // Calculate range for selected date (local time)
                 // selectedDate is "YYYY-MM-DD"
                 const [year, month, day] = selectedDate.split('-').map(Number);
                 
                 // Create date in local timezone
                 const start = new Date(year, month - 1, day, 0, 0, 0, 0);
                 const end = new Date(year, month - 1, day, 23, 59, 59, 999);
                 
                 // API expects seconds
                 pageRef.current = {
                     before: end.getTime() / 1000, after: start.getTime() / 1000, cursor: undefined,
                 };
            } else if (!append) {
                 pageRef.current = { before: Date.now() / 1000, after: 0, cursor: undefined };
            }

            const page = pageRef.current;
            const result = await getPhotosWithTotal(
                cameraId, page.before, page.after, 50, false, page.cursor, controller.signal,
            );
            if (!isCurrent()) return;
            const nextCursor = result.photos.at(-1)?.photo_cursor;
            const more = result.total > result.photos.length && result.photos.length > 0;
            if (more && (!nextCursor || nextCursor === page.cursor)) throw new Error("Invalid photo cursor");
            setPhotos(previous => append ? [...previous, ...result.photos] : result.photos);
            setHasHistory(append);
            pageRef.current = { ...page, cursor: nextCursor };
            setHasMore(more);
        } catch (e) {
            if (isCurrent()) {
                console.error("Failed to fetch timeline photos", e);
                setLoadError(t({ zh: "照片列表加载失败，请重试", en: "Failed to load photos. Please retry." }));
            }
        } finally {
            if (isCurrent()) setLoading(false);
        }
    }, [cameraId, selectedDate, t]);

    const pageRefresh = usePageRefresh({
        pageKey: `timeline:${cameraId}:${selectedDate}`, enabled: cameraId > 0,
        canAutoRefresh: !selectedDate && !hasHistory && !selectedLeftPhotoId && !selectedRightPhotoId && !loading && !isNavigating,
        refresh: async () => {
            await refreshUserInfo();
            await fetchPhotos();
        },
    });
    useEffect(() => {
        setPhotos([]);
        setHasMore(false);
        setHasHistory(false);
        return () => {
            requestRef.current.seq++;
            requestRef.current.controller?.abort();
            navigationRef.current?.abort();
        };
    }, [cameraId, selectedDate]);

    useEffect(() => {
        const focusId = selectedLeftPhotoId ?? selectedRightPhotoId ?? null;
        if (!focusId) return;
        setSelectedPhotoId(focusId);
        const elem = itemRefs.current.get(focusId) ?? null;
        if (!elem) return;
        elem.scrollIntoView({ block: "nearest", inline: "start" });
    }, [selectedLeftPhotoId, selectedRightPhotoId, photos.length]);

    // Group photos by date
    const groupedPhotos = photos.reduce((acc, photo) => {
        const dateStr = formatDate(photo.photo_time);
        if (!acc[dateStr]) {
            acc[dateStr] = [];
        }
        acc[dateStr].push(photo);
        return acc;
    }, {} as Record<string, PhotoInfo[]>);

    // Helper to jump to available date
    const jumpToDate = useCallback(async (direction: -1 | 1) => {
        if (!cameraId) return;
        
        // Determine reference timestamp
        let referenceTime: number;
        if (selectedDate) {
            const [y, m, d] = selectedDate.split('-').map(Number);
            const date = new Date(y, m - 1, d);
            if (direction === -1) {
                 // Search before Start of Day
                 date.setHours(0,0,0,0);
                 referenceTime = Math.floor(date.getTime() / 1000);
            } else {
                 // Search after End of Day
                 date.setHours(23,59,59,999);
                 referenceTime = Math.floor(date.getTime() / 1000);
            }
        } else {
            // If "Latest" is selected
            if (direction === 1) return; // Can't go future
            // For previous, search before now
            referenceTime = Math.floor(Date.now() / 1000);
        }
        
        setIsNavigating(true);
        navigationRef.current?.abort();
        const controller = new AbortController();
        navigationRef.current = controller;
        try {
            let result: PhotoInfo[] = [];
            if (direction === -1) {
                // Find latest photo BEFORE referenceTime
                // getPhotos(camId, before=referenceTime, after=0, limit=1, reverse=false)
                result = await getPhotos(cameraId, referenceTime - 0.001, 0, 1, false, undefined, controller.signal);
            } else {
                 // Find earliest photo AFTER referenceTime
                 // getPhotos(camId, before=now, after=referenceTime, limit=1, reverse=true)
                 result = await getPhotos(cameraId, undefined, referenceTime + 1, 1, true, undefined, controller.signal);
            }
            
            if (controller.signal.aborted) return;
            if (result.length > 0) {
                const photoTime = result[0].photo_time;
                const newDate = new Date(photoTime * 1000);
                const y = newDate.getFullYear();
                const m = String(newDate.getMonth() + 1).padStart(2, '0');
                const d = String(newDate.getDate()).padStart(2, '0');
                setSelectedDate(`${y}-${m}-${d}`);
            } else {
                // Could add toast here
                console.log("No photos found in that direction");
            }
        } catch (e) {
            console.error("Smart jump failed", e);
        } finally {
            if (navigationRef.current === controller) setIsNavigating(false);
        }
    }, [selectedDate, cameraId]);

    // Smart Date Formatter
    const getSmartDateString = useCallback((dateStr: string) => {
        if (!dateStr) return t({ zh: "最新照片", en: "Latest Photos" });
        
        const now = new Date();
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        
        // Reset hours for comparison
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        const diffTime = today.getTime() - target.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        
        const weekday = date.toLocaleDateString(t({ zh: 'zh-CN', en: 'en-US' }), { weekday: 'short' });
        const datePart = date.toLocaleDateString(t({ zh: 'zh-CN', en: 'en-US' }), { month: 'short', day: 'numeric' });
        
        if (diffDays === 0) {
            return `${t({ zh: "今天", en: "Today" })} ${datePart} ${weekday}`;
        } else if (diffDays === 1) {
            return `${t({ zh: "昨天", en: "Yesterday" })} ${datePart} ${weekday}`;
        } else if (diffDays === 2) {
            return `${t({ zh: "前天", en: "2 Days Ago" })} ${datePart} ${weekday}`;
        } else {
            return `${dateStr} ${weekday}`;
        }
    }, [t]);

    return (
        <div class="farpov_timeline_container">
            <div class="farpov_timeline_controls" style={{
                padding: "0.5rem 1rem",
                display: "flex",
                gap: "0.75rem",
                alignItems: "center",
                borderBottom: "1px solid var(--pico-muted-border-color)",
                fontSize: "0.85rem",
                color: "var(--pico-muted-color)",
                flexShrink: 0,
                flexWrap: "wrap"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Icon name="calendar" size={14} />
                    <span style={{ whiteSpace: "nowrap" }}>{t({ zh: "日期", en: "Date" })}:</span>
                </div>
                
                {/* Prev Available Day Button */}
                <button 
                    onClick={() => jumpToDate(-1)}
                    disabled={isNavigating}
                    title={t({ zh: "上一天 (有数据)", en: "Prev Available Day" })}
                    style={{
                        background: "var(--pico-card-background-color)",
                        border: "1px solid var(--pico-muted-border-color)",
                        borderRadius: "4px",
                        color: "var(--pico-primary)",
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: 0,
                        opacity: isNavigating ? 0.5 : 1
                    }}
                >
                    <Icon name="chevron-left" size={18} />
                </button>

                {/* Smart Date Display (Clickable to trigger hidden input) */}
                <div style={{ position: "relative", display: "inline-block", height: "28px" }}>
                    <div style={{
                        background: "var(--pico-card-background-color)",
                        border: "1px solid var(--pico-muted-border-color)",
                        borderRadius: "4px",
                        color: "var(--pico-color)",
                        padding: "0 0.75rem",
                        fontSize: "0.85rem",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "160px",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        fontWeight: 500
                    }}>
                        {getSmartDateString(selectedDate)}
                        <Icon name="chevron-down" size={14} style={{ marginLeft: "0.5rem", opacity: 0.5 }} />
                    </div>
                    
                    <input 
                        type="date" 
                        value={selectedDate} 
                        onChange={(e) => setSelectedDate(e.currentTarget.value)}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            cursor: "pointer",
                            zIndex: 10
                        }}
                    />
                </div>

                {/* Next Available Day Button */}
                <button 
                    onClick={() => jumpToDate(1)}
                    disabled={isNavigating}
                    title={t({ zh: "下一天 (有数据)", en: "Next Available Day" })}
                    style={{
                        background: "var(--pico-card-background-color)",
                        border: "1px solid var(--pico-muted-border-color)",
                        borderRadius: "4px",
                        color: "var(--pico-primary)",
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: 0,
                        opacity: isNavigating ? 0.5 : 1
                    }}
                >
                    <Icon name="chevron-right" size={18} />
                </button>
                
                {/* Quick Actions */}
                <button 
                    onClick={() => {
                        const now = new Date();
                        const y = now.getFullYear();
                        const m = String(now.getMonth() + 1).padStart(2, '0');
                        const d = String(now.getDate()).padStart(2, '0');
                        setSelectedDate(`${y}-${m}-${d}`);
                    }} 
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--pico-primary)",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        textDecoration: "underline",
                        padding: 0,
                        marginLeft: "0.5rem"
                    }}
                >
                    {t({ zh: "今天", en: "Today" })}
                </button>

                {selectedDate && (
                    <button 
                        onClick={() => setSelectedDate("")}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "var(--pico-primary)",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            textDecoration: "underline",
                            padding: 0
                        }}
                    >
                        {t({ zh: "显示最新", en: "Latest" })}
                    </button>
                )}
                
                <button 
                    onClick={pageRefresh.refreshNow}
                    title={t({ zh: "刷新", en: "Refresh" })}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--pico-muted-color)",
                        cursor: "pointer",
                        marginLeft: "auto",
                        opacity: 0.8,
                        padding: "0.2rem"
                    }}
                >
                    <Icon name="refresh-cw" size={14} />
                </button>
            </div>
            <div class="farpov_timeline_scroll" ref={scrollRef}>
                {pageRefresh.pendingRefresh && <small>{t({ zh: "页面已恢复，可更新照片", en: "Page resumed; photos can be refreshed" })}</small>}
                {pageRefresh.error && <p role="alert">{pageRefresh.error}</p>}
                {loading && <div class="farpov_timeline_loading">{t({ zh: "加载中...", en: "Loading..." })}</div>}
                {loadError && <div role="alert">{loadError}</div>}
                
                {Object.entries(groupedPhotos).map(([date, groupPhotos]) => (
                    <div class="farpov_timeline_group" key={date}>
                        <div class="farpov_timeline_date">
                            <Icon name="calendar" size={12} style={{ marginRight: "0.4rem" }} />
                            {date}
                        </div>
                        <div class="farpov_timeline_items">
                            {groupPhotos.map((photo) => (
                                (() => {
                                    const isLeftSelected = selectedLeftPhotoId === photo.photo_id;
                                    const isRightSelected = selectedRightPhotoId === photo.photo_id;
                                    const isFocused = selectedPhotoId === photo.photo_id;
                                    return (
                                <TimelineItem 
                                    key={photo.photo_id} 
                                    photo={photo} 
                                    isFocused={isFocused}
                                    isLeftSelected={isLeftSelected}
                                    isRightSelected={isRightSelected}
                                    itemRef={(el) => {
                                        if (el) itemRefs.current.set(photo.photo_id, el);
                                        else itemRefs.current.delete(photo.photo_id);
                                    }}
                                    onSelect={(target) => {
                                        setSelectedPhotoId(photo.photo_id);
                                        onSelectPhoto(photo, target);
                                    }}
                                    onClick={() => {
                                        // Update selected date display without triggering re-fetch if possible
                                        // But here selectedDate drives the fetch. 
                                        // If we want to just "display" the date, we might need a separate display state or just set selectedDate.
                                        // Setting selectedDate will trigger fetchPhotos.
                                        const date = new Date(photo.photo_time * 1000);
                                        const y = date.getFullYear();
                                        const m = String(date.getMonth() + 1).padStart(2, '0');
                                        const d = String(date.getDate()).padStart(2, '0');
                                        const dateStr = `${y}-${m}-${d}`;
                                        
                                        if (selectedDate !== dateStr) {
                                            setSelectedDate(dateStr);
                                        }
                                        setSelectedPhotoId(photo.photo_id);
                                    }}
                                />
                                    );
                                })()
                            ))}
                        </div>
                    </div>
                ))}
                {hasMore && (
                    <button type="button" disabled={loading} onClick={() => fetchPhotos(true)}>
                        {t({ zh: "加载更多", en: "Load More" })}
                    </button>
                )}
                {loadError && !hasMore && (
                    <button type="button" disabled={loading} onClick={() => fetchPhotos()}>
                        {t({ zh: "重试", en: "Retry" })}
                    </button>
                )}
            </div>
        </div>
    );
};

const TimelineItem: FunctionComponent<{ 
    photo: PhotoInfo, 
    isFocused: boolean,
    isLeftSelected: boolean,
    isRightSelected: boolean,
    itemRef?: (el: HTMLDivElement | null) => void,
    onSelect: (target: 0 | 1) => void,
    onClick?: () => void
}> = ({ photo, isFocused, isLeftSelected, isRightSelected, itemRef, onSelect, onClick }) => {
    const [imgUrl, setImgUrl] = useState<string>("");
    const [visible, setVisible] = useState(false);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const elementRef = useRef<HTMLDivElement | null>(null);
    
    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;
        if (typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }
        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setVisible(true);
                observer.disconnect();
            }
        }, { root: element.closest(".farpov_timeline_scroll"), rootMargin: "200px" });
        observer.observe(element);
        return () => observer.disconnect();
    }, [photo.photo_id]);

    useEffect(() => {
        setImgUrl("");
        setFailed(false);
        if (!visible) return;
        const controller = new AbortController();
        getPhotoImageURL(photo.photo_id, "timeline", controller.signal)
            .then(url => {
                if (controller.signal.aborted) return;
                setImgUrl(url);
            })
            .catch(err => {
                if (!controller.signal.aborted) {
                    console.warn(`Failed to load thumbnail for photo ${photo.photo_id}`, err);
                    if (attempt < 1) setAttempt(attempt + 1);
                    else setFailed(true);
                }
            });
        return () => {
            controller.abort();
        };
    }, [photo.photo_id, visible, attempt]);

    return (
        <div 
            class={`farpov_timeline_item ${isFocused ? "focused" : ""} ${isLeftSelected ? "selected-left" : ""} ${isRightSelected ? "selected-right" : ""}`}
            ref={el => { elementRef.current = el; itemRef?.(el); }}
            onClick={onClick}
        >
            <div class="farpov_timeline_thumb">
                {(isLeftSelected || isRightSelected) && (
                    <div class="farpov_timeline_side_badges">
                        {isLeftSelected && <span class="farpov_timeline_side_badge left">L</span>}
                        {isRightSelected && <span class="farpov_timeline_side_badge right">R</span>}
                    </div>
                )}
                {/* Use explicit width/height to force small rendering if CSS fails, 
                    but rely on CSS for actual layout. 
                    The backend thumbnail is already small (320px width usually). */}
                {imgUrl && <img key={imgUrl} src={imgUrl} alt={photo.photo_name} width="160" height="90"
                    onError={() => {
                        setImgUrl("");
                        if (attempt < 1) setAttempt(attempt + 1);
                        else setFailed(true);
                    }} />}
                {failed && (
                    <button type="button" aria-label="Retry thumbnail" onClick={e => {
                        e.stopPropagation();
                        setAttempt(value => value + 1);
                    }}><Icon name="refresh-cw" size={14} /></button>
                )}
            </div>
            <div class="farpov_timeline_time">{formatTime(photo.photo_time)}</div>
            
            <div class="farpov_timeline_actions">
                <button 
                    type="button" 
                    class={`timeline_action_btn left ${isLeftSelected ? "active" : ""}`}
                    onClick={(e: Event) => { e.stopPropagation(); onSelect(0); }}
                    title="Set as Left/Bottom"
                >
                    L
                </button>
                <button 
                    type="button" 
                    class={`timeline_action_btn right ${isRightSelected ? "active" : ""}`}
                    onClick={(e: Event) => { e.stopPropagation(); onSelect(1); }}
                    title="Set as Right/Top"
                >
                    R
                </button>
            </div>
        </div>
    );
};
